import { CommonModule, DatePipe } from "@angular/common";
import { Component, EventEmitter, inject, OnInit, Output } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { BehaviorSubject, combineLatest, map, Observable } from "rxjs";
import { TestCaseGeneratorActions } from "../../../core/ngrx/actions/testcase-generator.actions";
import { selectAllTestCaseVersions, selectLoading } from "../../../core/ngrx/selectors/testcase-generator.selectors";
import { ZorroTableComponent } from "../../common/custom-table/zorro-table.component";
import { LoadingComponent } from "../../common/loading/loading.component";
import { SimplePagerComponent } from "../../common/simple-pager/simple-pager.component";
import { tcgTestSuiteColumns } from "../../../core/table-templates/tcg-test-suites-table";

/** A single row of the "Test Suites" table. */
export interface TcgTestSuiteRow {
  id: string;
  testSuiteName: string;
  llmName: string;
  timestamp: string;
  testCasesCount: number;
  /** Pre-formatted label rendered by the "Actions" link cell. */
  actionsLabel: string;
  /** LLM connection id used when persisting a new suite (BPD / generate flows). */
  llmConnectionId?: string;
}

/**
 * "Test Suites" list view of the TCG (Test Case Generator) feature.
 * Matches Figma: https://www.figma.com/design/nHy94WssU9Eh6xhymPpxig/Helio-Assure-Accello-?node-id=10539-18159
 */
@Component({
  selector: "app-tcg-test-suites",
  standalone: true,
  imports: [CommonModule, SimplePagerComponent, ZorroTableComponent, LoadingComponent],
  templateUrl: "./tcg-test-suites.component.html",
  styleUrl: "./tcg-test-suites.component.scss",
})
export class TcgTestSuitesComponent implements OnInit {
  private readonly store    = inject(Store);
  private readonly route    = inject(ActivatedRoute);
  private readonly datePipe = new DatePipe('en-US');

  /** Emitted with the selected row when its "Actions" link is clicked. */
  @Output() viewTestCases = new EventEmitter<TcgTestSuiteRow>();

  columns = tcgTestSuiteColumns;

  readonly pageSize = 8;
  private readonly pageIndex$ = new BehaviorSubject(0);

  testSuites$!: Observable<TcgTestSuiteRow[]>;
  pagedSuites$!: Observable<TcgTestSuiteRow[]>;
  pager$!: Observable<{ start: number; end: number; total: number; disablePrev: boolean; disableNext: boolean }>;
  loading$!: Observable<boolean>;

  get projectId(): string {
    return this.route.snapshot.paramMap.get("id") ?? "";
  }

  ngOnInit(): void {
    this.loading$ = this.store.select(selectLoading);

    this.testSuites$ = this.store.select(selectAllTestCaseVersions).pipe(
      map((versions: any[]) =>
        versions.map((v): TcgTestSuiteRow => ({
          id:             v._id ?? v.id ?? "",
          testSuiteName:  v.versionName ?? "",
          llmName:        v.llmName ?? "-",
          timestamp:      this.formatTimestamp(v.timestamp),
          testCasesCount: v.testcaseCount ?? 0,
          actionsLabel:   `${v.testcaseCount ?? 0} Test Cases`,
          llmConnectionId: v.llmConnectionId ?? v.llmConnection ?? undefined,
        }))
      )
    );

    this.pagedSuites$ = combineLatest([this.testSuites$, this.pageIndex$]).pipe(
      map(([suites, page]) => suites.slice(page * this.pageSize, (page + 1) * this.pageSize))
    );

    this.pager$ = combineLatest([this.testSuites$, this.pageIndex$]).pipe(
      map(([suites, page]) => {
        const total      = suites.length;
        const totalPages = Math.ceil(total / this.pageSize);
        const start      = total === 0 ? 0 : page * this.pageSize + 1;
        const end        = Math.min((page + 1) * this.pageSize, total);
        return {
          start,
          end,
          total,
          disablePrev: page === 0,
          disableNext: page >= totalPages - 1,
        };
      })
    );

    this.store.dispatch(
      TestCaseGeneratorActions.loadTestCaseVersions({ projectId: this.projectId })
    );
  }

  prevPage(): void {
    if (this.pageIndex$.value > 0) {
      this.pageIndex$.next(this.pageIndex$.value - 1);
    }
  }

  nextPage(): void {
    this.pageIndex$.next(this.pageIndex$.value + 1);
  }

  onTableAction(event: any): void {
    const row = event?.data?.row as TcgTestSuiteRow | undefined;
    if (!row?.id) return;

    if (event?.type === "link" || event?.data?.column?.key === "actionsLabel") {
      this.store.dispatch(
        TestCaseGeneratorActions.loadTestCaseDetailsByVersionId({
          projectId: this.projectId,
          versionId: row.id,
        })
      );
      this.viewTestCases.emit(row);
    }
  }

  private formatTimestamp(raw: string | null | undefined): string {
    if (!raw) return "-";

    // API may return "yyyy-MM-dd_HH:mm:ss" — normalize underscore to space for display.
    const normalized = String(raw).trim().replace("_", " ");
    const date = new Date(normalized.includes("T") || normalized.includes(" ")
      ? normalized.replace(" ", "T")
      : normalized);

    if (isNaN(date.getTime())) {
      return normalized;
    }
    return this.datePipe.transform(date, "yyyy-MM-dd HH:mm:ss") ?? normalized;
  }
}
